import axios from 'axios';
import {formatISO} from 'date-fns';

import {getBearerToken} from '@/api/gateway';

export const isAuthenticated = () => {
    try {
        return getBearerToken() != null;
    } catch (error) {
        return false;
    }
}

export const appId = () => {
    return window.appId;
}

export const appCallbackRoute = async () => {
    return window.app().routes.index
}

export const config = (key) => {
    return window.config(key);
}

export const ssoAuthentication = async (id) => {
    if (config('auth.clientId') && config('auth.clientId')) {
        return config('auth');
    }

    throw new Error(`The app '${id}' does not support sso auth`);
}

export const redirectToSSO = () => {
    window.location = config('auth.authorizationUrl') +
        "&continue=" + encodeURI(config('auth.returnUrl'))
}

export function formatDate(date) {
    if (!date) {
        return undefined;
    }
    return formatISO(date, {representation: 'date'});
}

export async function listRegions() {
    return axios
        .get(`${window.config('access.url')}/region`)
        .then(response => response.data.data);
}

export async function listProductionTypes() {

    return axios
        .get(`${window.config('access.url')}/production-type`)
        .then(response => response.data.data);
}

export async function listIndustries() {

    return axios
        .get(`${window.config('access.url')}/industry-types`)
        .then(response => response.data.data);
}

export async function listFeedbacks() {
    return axios
        .get(`${window.config('workflow.url')}/workflow/feedback/all`,
            {
                headers: {
                    Authorization: `Bearer ${getBearerToken()}`
                }
            })
        .then(response => response.data);
}

export async function listTikTok() {
  return axios
    .get(`${window.config('workflow.url')}/workflow/tiktok/all`,
      {
        headers: {
          Authorization: `Bearer ${getBearerToken()}`
        }
      })
    .then(response => response.data);
}

export async function listFeedbacksProject(projectId, userId) {
    return axios
        .get(`${window.config('workflow.url')}/workflow/feedback/${projectId}/${userId}`, {
            headers: {
                Authorization: `Bearer ${getBearerToken()}`
            }
        })
        .then(response => response.data);
}

export async function setFeedbackData(feedback, projectId, userId) {

    return axios
        .post(`${window.config('workflow.url')}/workflow/feedback/${projectId}/${userId}`,
            {...feedback},
            {
                headers: {
                    Authorization: `Bearer ${getBearerToken()}`
                }
            })
        .then(response => response.data);
}

export async function patchFeedbackData(feedback, projectId, userId) {

    return axios
        .patch(`${window.config('workflow.url')}/workflow/feedback/${projectId}/${userId}`,
            {...feedback},
            {
                headers: {
                    Authorization: `Bearer ${getBearerToken()}`
                }
            })
        .then(response => response.data);
}


export async function listCampaignObjectives() {

    return axios
        .get(`${window.config('access.url')}/campaign-objective`)
        .then(response => response.data.data);
}

export async function listCommunicationObjectives() {

    return axios
        .get(`${window.config('access.url')}/communication-objectives`)
        .then(response => response.data.data);
}

export async function listCreativeTechnic() {

    return axios
        .get(`${window.config('access.url')}/creative-technic`)
        .then(response => response.data.data);
}

export async function publishToAccess(asset) {

    return axios
        .post(`${window.config('workflow.url')}/workflow/projects/${asset.projectId}/delivery/final-assets/${asset.id}/publish`,
            {...asset, target: 'the-source'},
            {
                headers: {
                    Authorization: `Bearer ${getBearerToken()}`
                }
            })
        .then(response => response.data.data);
}

export async function listCreators() {

    return axios
        .get(`${window.config('access.url')}/creator`)
        .then(response => response.data.data);
}

export async function listBrandsFromAccess() {

    return axios
        .get(`${window.config('access.url')}/brand?per_page=10000`)
        .then(response => response.data.data);
}

export async function listBrands() {

    return axios
        .get(`${window.config('workflow.url')}/workflow/brands`, {
            headers: {
                Authorization: `Bearer ${getBearerToken()}`
            }
        })
        .then(response => response.data.data)
        .then(brands =>
            brands.map(brand => {
                const pictureUrl = brand.logo && brand.logo.length && brand.logo.find(l => l.key === 'LOGO')?.url;
                return {
                    ...brand,
                    pictureUrl
                };
            })
        );
}

export async function listBrandsManual() {
    return axios
        .get(`${window.config('workflow.url')}/workflow/brands`, {
            headers: {
                Authorization: `Bearer ${getBearerToken()}`
            }
        })
        .then(response => response.data.data);
}

export async function listOptions() {

    return axios
        .get(`${window.config('workflow.url')}/workflow/options`, {
            headers: {
                Authorization: `Bearer ${getBearerToken()}`
            }
        })
        .then(response => response.data.data);
}

export async function listUsers() {

    return axios
        .get(`${window.config('workflow.url')}/workflow/users`, {
            headers: {
                Authorization: `Bearer ${getBearerToken()}`
            }
        })
        .then(response => response.data.data);
}

export async function listUploads({projectId, filters = {}}) {
    const payload = {
        projectId,
        ...filters
    };
    return axios
        .get(`${window.config('workflow.url')}/workflow/uploads`, {
            params: payload,
            headers: {
                Authorization: `Bearer ${getBearerToken()}`
            }
        })
        .then(response => response.data.data);
}

export async function listProducts() {
    return axios
        .get(window.config('auth.products'), {
            headers: {
                Authorization: `Bearer ${getBearerToken()}`
            }
        })
        .then(response => response.data.data);
}

export async function listAssets({projectId, filters = {}}) {
    const payload = {
        projectId,
        ...filters
    };
    return axios
        .get(`${window.config('workflow.url')}/workflow/assets`, {
            params: payload,
            headers: {
                Authorization: `Bearer ${getBearerToken()}`
            }
        })
        .then(response => response.data.data);
}

export async function listFileRequests({projectIds, filters = {}}) {
    const payload = {
        projectIds,
        ...filters
    };

    return axios
        .get(`${window.config('workflow.url')}/workflow/projects/assets/requests/all`, {
            params: payload,
            headers: {
                Authorization: `Bearer ${getBearerToken()}`
            }
        })
        .then(response => response.data.data);
}

export async function fetchProjectResourceByToken({token}) {

    return axios
        .get(`${window.config('workflow.url')}/workflow/projects/sharings/resources?token=${token}`)
        .then(response => response.data.data);
}

export async function fileRequestByToken({token}) {

    return axios
        .get(`${window.config('workflow.url')}/workflow/assets/requests/find?token=${token}`)
        .then(response => response.data.data);
}

export async function shareFileRequestByMail({token, email, message}) {

    return axios
        .post(`${window.config('workflow.url')}/workflow/projects/assets/requests/invite/${token}`, {email, message}, {
            headers: {
                Authorization: `Bearer ${getBearerToken()}`
            }
        })
        .then(response => response.data.data);
}

export async function uploadAssetUsingFileRequest({
                                                      projectId,
                                                      name,
                                                      token,
                                                      fileRequestId,
                                                      fileId,
                                                      group,
                                                      type,
                                                      url,
                                                      lastUpload,
                                                      finalAssets,
                                                      workingFiles
                                                  }) {

    return axios
        .post(`${window.config('workflow.url')}/workflow/projects/${projectId}/assets/requests/${fileRequestId}/uploads`, {
            name,
            url,
            group,
            fileId,
            type,
            lastUpload,
            finalAssets,
            workingFiles,
            token
        })
        .then(response => response.data.data);
}

export async function createProjectFileRequest({projectId, name, description, deadline, flexible, group, platforms}) {
    const payload = {
        name,
        description,
        deadline,
        flexible,
        group,
        platforms
    };

    return axios
        .post(`${window.config('workflow.url')}/workflow/projects/${projectId}/assets/requests`, payload, {
            headers: {
                Authorization: `Bearer ${getBearerToken()}`
            }
        })
        .then(response => response.data.data);
}

export async function updateProjectFileRequest({
                                                   projectId,
                                                   fileRequestId,
                                                   name,
                                                   description,
                                                   flexible,
                                                   deadline,
                                                   platforms,
                                                   archived
                                               }) {
    const payload = {
        name,
        description,
        flexible,
        platforms,
        deadline,
        archived
    };

    return axios
        .patch(`${window.config('workflow.url')}/workflow/projects/${projectId}/assets/requests/${fileRequestId}`, payload, {
            headers: {
                Authorization: `Bearer ${getBearerToken()}`
            }
        })
        .then(response => response.data.data);
}

export async function deleteProjectFileRequest({projectId, fileRequestId}) {

    return axios
        .delete(`${window.config('workflow.url')}/workflow/projects/${projectId}/assets/requests/${fileRequestId}`, {
            headers: {
                Authorization: `Bearer ${getBearerToken()}`
            }
        })
        .then(response => response.data.data);
}

export async function createFolder({name, project_type, brands, partner, groups, industry, country}) {
    const payload = {
        name,
        project_type,
        brands,
        partner,
        groups,
        industry,
        country
    };

    return axios
        .post(`${window.config('workflow.url')}/workflow/folders`, payload, {
            headers: {
                Authorization: `Bearer ${getBearerToken()}`
            }
        })
        .then(response => response.data.data);
}

export async function archiveFolder(id) {

    return axios
        .delete(`${window.config('workflow.url')}/workflow/folders/${id}`, {
            headers: {
                Authorization: `Bearer ${getBearerToken()}`
            }
        })
        .then(response => response.data.data);
}

export async function updateFolder({name, id}) {
    const payload = {
        name,
    };

    return axios
        .put(`${window.config('workflow.url')}/workflow/folders/${id}`, payload, {
            headers: {
                Authorization: `Bearer ${getBearerToken()}`
            }
        })
        .then(response => response.data.data);
}

export async function updateUploadRecord({assets, id}) {
    const payload = {
        assets
    };

    return axios
        .put(`${window.config('workflow.url')}/workflow/uploads/${id}`, payload, {
            headers: {
                // Authorization: `Bearer ${getBearerToken()}`
            }
        })
        .then(response => response.data.data);
}

export async function createUploadRecord({projectId, assetRequestId}) {
    const payload = {
        project_id: projectId,
        asset_requests_id: assetRequestId
    };

    return axios
        .post(`${window.config('workflow.url')}/workflow/uploads`, payload, {
            headers: {
                // Authorization: `Bearer ${getBearerToken()}`
            }
        })
        .then(response => response.data.data);
}

export async function createProject({
                                        name,
                                        parentProjectId,
                                        brandIds,
                                        dueDate,
                                        status,
                                        folderId,
                                        folder,
                                        projectType,
                                        partner,
                                        program,
                                        platforms,
                                        newBrands,
                                        newGroups,
                                        country,
                                        group,
                                        productionType,
                                        industry
                                    }) {
    const payload = {
        name,
        parentProjectId,
        brandIds,
        folderId,
        folder,
        dueDate: formatDate(dueDate),
        status,
        projectType,
        program,
        partner,
        platforms,
        newBrands,
        newGroups,
        country,
        group,
        productionType,
        industry
    };

    return axios
        .post(`${window.config('workflow.url')}/workflow/projects`, payload, {
            headers: {
                Authorization: `Bearer ${getBearerToken()}`
            }
        })
        .then(response => response.data.data);
}

export async function listProjects({projectId, filters = {}}) {
    const payload = {
        projectId,
        ...filters,
        dueDate: formatDate(filters.dueDate)
    };

    return axios
        .get(`${window.config('workflow.url')}/workflow/projects`, {
            params: payload,
            headers: {
                Authorization: `Bearer ${getBearerToken()}`
            }
        })
        .then(response => response.data.data)
        .then(data => ({
            ...data,
            brands: data.brands.map(brand => {
                const pictureUrl = brand.logo && brand.logo.length && brand.logo.find(l => l.key === 'LOGO')?.url;
                return {
                    ...brand,
                    pictureUrl
                };
            })
        }));
}

export async function updateProject({
                                        project,
                                        name,
                                        parentProjectId,
                                        brandIds,
                                        newBrands,
                                        dueDate,
                                        status,
                                        folder,
                                        projectType,
                                        partner,
                                        platform,
                                        program,
                                        productionType,
                                        country,
                                        group,
                                        industry
                                    }) {
    const payload = {
        name,
        parentProjectId,
        brandIds,
        dueDate: formatDate(dueDate),
        status,
        folder,
        newBrands,
        projectType,
        partner,
        platform,
        program,
        productionType,
        country,
        group,
        industry
    };

    return axios
        .patch(`${window.config('workflow.url')}/workflow/projects/${project.id}`, payload, {
            headers: {
                Authorization: `Bearer ${getBearerToken()}`
            }
        })
        .then(response => response.data.data);
}

export async function deleteProject({project}) {

    return axios
        .delete(`${window.config('workflow.url')}/workflow/projects/${project.id}`, {
            headers: {
                Authorization: `Bearer ${getBearerToken()}`
            }
        })
        .then(response => response.data.data);
}

export async function exportProjects() {

    return axios({
        url: `${window.config('workflow.url')}/workflow/projects/export`,
        method: 'GET',
        responseType: 'blob',
        headers: {
            Authorization: `Bearer ${getBearerToken()}`
        }
    })
        .then(response => {
            const date = new Date();

            let filename = `Export_${date.getFullYear()}-${date.getUTCMonth()}-${date.getDate()}-${date.getHours()}-${date.getMinutes()}.csv`;
            return {
                filename,
                file: response.data
            };
        });
}

export async function updateProjectField({project, field, value}) {
    const payload = {
        [field]: value
    };

    return axios
        .patch(`${window.config('workflow.url')}/workflow/projects/${project.id}`, payload, {
            headers: {
                Authorization: `Bearer ${getBearerToken()}`
            }
        })
        .then(response => response.data.data);
}


export async function shareProjectResource({projectId, resource, extras, description}) {
    const payload = {
        resource,
        extras,
        description
    };

    return axios
        .patch(`${window.config('workflow.url')}/workflow/projects/${projectId}/sharings`, payload, {
            headers: {
                Authorization: `Bearer ${getBearerToken()}`
            }
        })
        .then(response => response.data.data);
}

export async function getProjectBrief({projectId}) {

    return axios
        .get(`${window.config('workflow.url')}/workflow/projects/${projectId}/brief`)
        .then(response => response.data.data);
}

export async function updateSharedProjectBrief({projectId, brief, token}) {
    const payload = {
        ...brief
    };

    return axios
        .put(`${window.config('workflow.url')}/workflow/projects/${token}/sharings/brief`, payload)
        .then(response => response.data.data);
}

export async function updateProjectBrief({projectId, brief}) {
    const payload = {
        ...brief
    };

    return axios
        .put(`${window.config('workflow.url')}/workflow/projects/${projectId}/brief`, payload)
        .then(response => response.data.data);
}

export async function updateProjectBriefField({projectId, field, value}) {
    const payload = {
        [field]: value
    };

    return axios
        .patch(`${window.config('workflow.url')}/workflow/projects/${projectId}/brief`, payload)
        .then(response => response.data.data);
}

export async function createProjectAsset({projectId, name, group, type, url, fileId}) {
    const payload = {
        name,
        group,
        type,
        url,
        fileId
    };

    return axios
        .post(`${window.config('workflow.url')}/workflow/projects/${projectId}/assets`, payload, {
            headers: {
                Authorization: `Bearer ${getBearerToken()}`
            }
        })
        .then(response => response.data.data);
}

export async function updateProjectAsset({projectId, assetId, name}) {
    const payload = {
        name
    };

    return axios
        .patch(`${window.config('workflow.url')}/workflow/projects/${projectId}/assets/${assetId}`, payload, {
            headers: {
                Authorization: `Bearer ${getBearerToken()}`
            }
        })
        .then(response => response.data.data);
}

export async function deleteProjectAsset({projectId, assetId}) {

    return axios
        .delete(`${window.config('workflow.url')}/workflow/projects/${projectId}/assets/${assetId}`, {
            headers: {
                Authorization: `Bearer ${getBearerToken()}`
            }
        })
        .then(response => response.data.data);
}

export async function getProjectProductionSteps({projectId}) {

    return axios
        .get(`${window.config('workflow.url')}/workflow/projects/${projectId}/production/steps`, {
            headers: {
                Authorization: `Bearer ${getBearerToken()}`
            }
        })
        .then(response => response.data.data);
}

export async function createProjectProductionStep({projectId, name, url, userIds}) {
    const payload = {
        name,
        url,
        userIds,
        type: 'filestage'
    };

    return axios
        .post(`${window.config('workflow.url')}/workflow/projects/${projectId}/production/steps`, payload, {
            headers: {
                Authorization: `Bearer ${getBearerToken()}`
            }
        })
        .then(response => response.data.data);
}

export async function updateProjectProductionStep({projectId, stepId, name, url, userIds}) {
    const payload = {
        name,
        url,
        userIds
    };

    return axios
        .patch(`${window.config('workflow.url')}/workflow/projects/${projectId}/production/steps/${stepId}`, payload, {
            headers: {
                Authorization: `Bearer ${getBearerToken()}`
            }
        })
        .then(response => response.data.data);
}

export async function deleteProjectProductionStep({projectId, stepId}) {

    return axios
        .delete(`${window.config('workflow.url')}/workflow/projects/${projectId}/production/steps/${stepId}`, {
            headers: {
                Authorization: `Bearer ${getBearerToken()}`
            }
        })
        .then(response => response.data.data);
}

export async function getProjectFinalAssets({projectId}) {

    return axios
        .get(`${window.config('workflow.url')}/workflow/projects/${projectId}/delivery/final-assets`, {
            headers: {
                Authorization: `Bearer ${getBearerToken()}`
            }
        })
        .then(response => response.data.data);
}

export async function createProjectFinalAsset({projectId, name, type, fileId, platforms, lastUpload}) {
    const payload = {
        name,
        type,
        fileId,
        lastUpload,
        platforms
    };

    return axios
        .post(`${window.config('workflow.url')}/workflow/projects/${projectId}/delivery/final-assets`, payload, {
            headers: {
                Authorization: `Bearer ${getBearerToken()}`
            }
        })
        .then(response => response.data.data);
}

export async function updateProjectFinalAsset({projectId, assetId, name, platforms}) {
    const payload = {
        name,
        platforms
    };

    return axios
        .patch(`${window.config('workflow.url')}/workflow/projects/${projectId}/delivery/final-assets/${assetId}`, payload, {
            headers: {
                Authorization: `Bearer ${getBearerToken()}`
            }
        })
        .then(response => response.data.data);
}

export async function deleteProjectFinalAsset({projectId, assetId}) {

    return axios
        .delete(`${window.config('workflow.url')}/workflow/projects/${projectId}/delivery/final-assets/${assetId}`, {
            headers: {
                Authorization: `Bearer ${getBearerToken()}`
            }
        })
        .then(response => response.data.data);
}

export async function getProjectWorkingFiles({projectId}) {

    return axios
        .get(`${window.config('workflow.url')}/workflow/projects/${projectId}/delivery/working-files`, {
            headers: {
                Authorization: `Bearer ${getBearerToken()}`
            }
        })
        .then(response => response.data.data);
}

export async function createProjectWorkingFile({projectId, name, type, fileId, url, userIds}) {
    const payload = {
        name,
        type,
        fileId,
        url,
        userIds
    };

    return axios
        .post(`${window.config('workflow.url')}/workflow/projects/${projectId}/delivery/working-files`, payload, {
            headers: {
                Authorization: `Bearer ${getBearerToken()}`
            }
        })
        .then(response => response.data.data);
}

export async function updateProjectWorkingFile({projectId, assetId, name, userIds}) {
    const payload = {
        name,
        userIds
    };

    return axios
        .patch(`${window.config('workflow.url')}/workflow/projects/${projectId}/delivery/working-files/${assetId}`, payload, {
            headers: {
                Authorization: `Bearer ${getBearerToken()}`
            }
        })
        .then(response => response.data.data);
}

export async function deleteProjectWorkingFile({projectId, assetId}) {

    return axios
        .delete(`${window.config('workflow.url')}/workflow/projects/${projectId}/delivery/working-files/${assetId}`, {
            headers: {
                Authorization: `Bearer ${getBearerToken()}`
            }
        })
        .then(response => response.data.data);
}

export async function getProjectDocuments({projectId}) {

    return axios
        .get(`${window.config('workflow.url')}/workflow/projects/${projectId}/documents`, {
            headers: {
                Authorization: `Bearer ${getBearerToken()}`
            }
        })
        .then(response => response.data.data);
}

export async function createProjectDocument({projectId, name, type, group, fileId, userIds}) {
    const payload = {
        name,
        type,
        group,
        fileId,
        userIds
    };

    return axios
        .post(`${window.config('workflow.url')}/workflow/projects/${projectId}/documents`, payload, {
            headers: {
                Authorization: `Bearer ${getBearerToken()}`
            }
        })
        .then(response => response.data.data);
}

export async function updateProjectDocument({projectId, documentId, name, userIds}) {
    const payload = {
        name,
        userIds
    };

    return axios
        .patch(`${window.config('workflow.url')}/workflow/projects/${projectId}/documents/${documentId}`, payload, {
            headers: {
                Authorization: `Bearer ${getBearerToken()}`
            }
        })
        .then(response => response.data.data);
}

export async function deleteProjectDocument({projectId, documentId}) {

    return axios
        .delete(`${window.config('workflow.url')}/workflow/projects/${projectId}/documents/${documentId}`, {
            headers: {
                Authorization: `Bearer ${getBearerToken()}`
            }
        })
        .then(response => response.data.data);
}

export async function downloadProjectAssets({projectId, assetIds}) {
    const payload = {
        assetIds
    };

    return axios
        .get(`${window.config('workflow.url')}/workflow/projects/${projectId}/download/assets`, {
            params: payload,
            headers: {
                Authorization: `Bearer ${getBearerToken()}`
            },
            responseType: 'arraybuffer'
        })
        .catch(err => {
            console.error(`Server error when downloading assets ${assetIds.join(', ')} of project ${projectId}`, err);
            throw err;
        });
}

export async function downloadProjectFinalAssets({projectId, assetIds}) {
    const payload = {
        assetIds
    };

    return axios
        .get(`${window.config('workflow.url')}/workflow/projects/${projectId}/download/final-assets`, {
            params: payload,
            // headers: {
            //   Authorization: `Bearer ${getBearerToken()}`
            // },
            responseType: 'arraybuffer'
        })
        .catch(err => {
            console.error(`Server error when downloading final assets ${assetIds.join(', ')} of project ${projectId}`, err);
            throw err;
        });
}

export async function downloadProjectWorkingFiles({projectId, assetIds}) {
    const payload = {
        assetIds
    };

    return axios
        .get(`${window.config('workflow.url')}/workflow/projects/${projectId}/download/working-files`, {
            params: payload,
            // headers: {
            //   Authorization: `Bearer ${getBearerToken()}`
            // },
            responseType: 'arraybuffer'
        })
        .catch(err => {
            console.error(`Server error when downloading working files ${assetIds.join(', ')} of project ${projectId}`, err);
            throw err;
        });
}

export async function downloadProjectDocuments({projectId, assetIds}) {
    const payload = {
        assetIds
    };

    return axios
        .get(`${window.config('workflow.url')}/workflow/projects/${projectId}/download/documents`, {
            params: payload,
            headers: {
                Authorization: `Bearer ${getBearerToken()}`
            },
            responseType: 'arraybuffer'
        })
        .catch(err => {
            console.error(`Server error when downloading documents ${assetIds.join(', ')} of project ${projectId}`, err);
            throw err;
        });
}
